import React, { useState, useMemo, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Alert,
    CircularProgress,
    Typography,
    IconButton,
    Divider,
    Autocomplete,
    Chip,
    FormControl,
    FormLabel,
    FormControlLabel,
    Radio,
    RadioGroup,
} from "@mui/material";
import { Close, Save } from "@mui/icons-material";
import Cookies from "js-cookie";
import useNotification from "../common/Notification";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import { ROLE_LABELS } from "./roleLabels";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";

const MANAGED_ROLE_OPTIONS = [
    { value: "WB-USER", label: ROLE_LABELS["WB-USER"] },
    { value: "WB-AGENT", label: ROLE_LABELS["WB-AGENT"] },
    { value: "CRM-USER", label: ROLE_LABELS["CRM-USER"] },
];

const CreateSubUserModal = ({ open, onClose, onUserCreated }) => {
    /** When caller is both WB-ADMIN and CRM-ADMIN: pick one type per API (no CRM + WhatsApp mix in one request). */
    const [accountKind, setAccountKind] = useState("whatsapp");
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        roles: [],
        bwc: "",
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const notify = useNotification();

    /** `userRole` cookie: comma-separated admin flags. Re-read when the dialog opens. */
    const roleTokens = useMemo(() => {
        if (!open) return [];
        return String(Cookies.get("userRole") || "")
            .split(",")
            .map((r) => r.trim().toUpperCase())
            .filter(Boolean);
    }, [open]);

    const isWbAdmin = roleTokens.includes("WB-ADMIN");
    const isCrmAdmin = roleTokens.includes("CRM-ADMIN");

    const roleChoices = useMemo(() => {
        if (!isWbAdmin && !isCrmAdmin) return [];
        if (isWbAdmin && isCrmAdmin) {
            return accountKind === "crm"
                ? MANAGED_ROLE_OPTIONS.filter((o) => o.value === "CRM-USER")
                : MANAGED_ROLE_OPTIONS.filter((o) => o.value === "WB-USER" || o.value === "WB-AGENT");
        }
        if (isWbAdmin) return MANAGED_ROLE_OPTIONS.filter((o) => o.value === "WB-USER" || o.value === "WB-AGENT");
        return MANAGED_ROLE_OPTIONS.filter((o) => o.value === "CRM-USER");
    }, [isWbAdmin, isCrmAdmin, accountKind]);

    useEffect(() => {
        if (!open || roleChoices.length === 0) return;
        setFormData((prev) => {
            const kept = (prev.roles || []).filter((r) => roleChoices.some((o) => o.value === r));
            if (kept.length > 0) return { ...prev, roles: kept };
            return { ...prev, roles: [roleChoices[0].value] };
        });
    }, [open, roleChoices]);

    const needsBwc = formData.roles.some((r) => r === "WB-USER" || r === "WB-AGENT");

    const selectedRoleOptions = useMemo(
        () => roleChoices.filter((o) => formData.roles.includes(o.value)),
        [roleChoices, formData.roles]
    );

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setError("");
    };

    const validateForm = () => {
        if (!formData.name.trim()) {
            setError("Name is required");
            return false;
        }
        if (!formData.email.trim()) {
            setError("Email is required");
            return false;
        }
        if (!formData.email.includes("@")) {
            setError("Please enter a valid email address");
            return false;
        }
        if (!formData.password.trim()) {
            setError("Password is required");
            return false;
        }
        if (formData.password.length < 6) {
            setError("Password must be at least 6 characters long");
            return false;
        }
        if (!formData.roles || formData.roles.length === 0) {
            setError("Select at least one role");
            return false;
        }
        const hasCrm = formData.roles.includes("CRM-USER");
        const hasWb = formData.roles.some((r) => r === "WB-USER" || r === "WB-AGENT");
        if (hasCrm && hasWb) {
            setError(
                "CRM access and WhatsApp business roles cannot be created together in one step. Switch user type and try again."
            );
            return false;
        }
        if (needsBwc) {
            const raw = formData.bwc === "" ? "0" : String(formData.bwc);
            if (raw.trim() !== "" && (Number.isNaN(Number(raw)) || Number(raw) < 0)) {
                setError("WhatsApp credits (BWC) must be zero or a positive number");
                return false;
            }
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setError("");

        try {
            const roles = formData.roles;
            const bwc = needsBwc ? Math.max(0, parseInt(formData.bwc === "" ? "0" : formData.bwc, 10) || 0) : 0;

            // POST /api/users/managed: body key must be user_role (comma-separated → users.user_role). See API_DOCUMENT.md.
            const user_role = roles.length === 1 ? roles[0] : roles.join(",");

            const requestData = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                password: formData.password,
                user_role,
                ...(needsBwc ? { bwc } : {}),
            };

            const response = await axiosInstance.post(`${base_url}/users/managed`, requestData, {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
            });

            const ok = response.status === 201 || response.data?.success === true;
            if (ok) {
                notify(response.data?.message || "User created successfully", "success");
                handleClose();
                if (onUserCreated) onUserCreated();
            } else {
                throw new Error(response.data?.message || "Failed to create user");
            }
        } catch (err) {
            console.error("Error creating managed user:", err);
            let errorMessage = "Failed to create user";

            if (err.response?.data) {
                if (err.response.data.errors) {
                    const errors = err.response.data.errors;
                    errorMessage = Object.values(errors).flat().join(", ");
                } else if (err.response.data.message) {
                    errorMessage = err.response.data.message;
                } else if (err.response.data.error) {
                    errorMessage = err.response.data.error;
                }
            }

            setError(errorMessage);
            notify(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setAccountKind("whatsapp");
        setFormData({
            name: "",
            email: "",
            password: "",
            roles: [],
            bwc: "",
        });
        setError("");
        setLoading(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, border: `1px solid ${WB_UI.color.border}` } }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 2,
                    px: 2.5,
                    pb: 1.5,
                }}
            >
                <Typography sx={{ fontWeight: 700, fontSize: "1rem", letterSpacing: "-0.01em" }}>
                    Create managed user
                </Typography>
                <IconButton onClick={handleClose} size="small" aria-label="Close" sx={{ color: "text.secondary" }}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>
            <Divider />

            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ pt: 2.5 }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {roleChoices.length === 0 ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Only a <strong>WhatsApp business admin</strong> or <strong>CRM Admin</strong> can create users
                            from this screen.
                        </Alert>
                    ) : null}

                    {isWbAdmin && isCrmAdmin ? (
                        <FormControl component="fieldset" variant="standard" sx={{ mb: 1 }}>
                            <FormLabel
                                component="legend"
                                sx={{ fontSize: "0.75rem", fontWeight: 700, color: "text.secondary", mb: 0.5 }}
                            >
                                What kind of account are you creating?
                            </FormLabel>
                            <RadioGroup
                                row
                                value={accountKind}
                                onChange={(e) => {
                                    setAccountKind(e.target.value);
                                    setError("");
                                }}
                            >
                                <FormControlLabel
                                    value="whatsapp"
                                    control={<Radio size="small" />}
                                    label={<Typography variant="body2">WhatsApp business</Typography>}
                                />
                                <FormControlLabel
                                    value="crm"
                                    control={<Radio size="small" />}
                                    label={<Typography variant="body2">CRM</Typography>}
                                />
                            </RadioGroup>
                        </FormControl>
                    ) : null}

                    {needsBwc ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Note:</strong> WhatsApp credits you assign are taken from your own business pool when
                                the amount is greater than zero.
                            </Typography>
                        </Alert>
                    ) : null}
                    {formData.roles.includes("CRM-USER") ? (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                CRM access is tied to your CRM Admin account; no separate WhatsApp setup is created for that
                                part.
                            </Typography>
                        </Alert>
                    ) : null}

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 0.5 }}>
                        <Autocomplete
                            multiple
                            disabled={loading || roleChoices.length === 0}
                            options={roleChoices}
                            getOptionLabel={(o) => o.label}
                            isOptionEqualToValue={(a, b) => a.value === b.value}
                            value={selectedRoleOptions}
                            onChange={(_, newValue) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    roles: newValue.map((o) => o.value),
                                }));
                                setError("");
                            }}
                            disableCloseOnSelect
                            filterSelectedOptions
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={option.value}
                                        label={option.label}
                                        size="small"
                                        sx={{ fontWeight: 600 }}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Roles *"
                                    placeholder={roleChoices.length ? "Choose one or more" : ""}
                                    size="small"
                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: WB_UI.radius.card } }}
                                />
                            )}
                        />

                        <TextField
                            fullWidth
                            label="Name *"
                            name="name"
                            type="text"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Enter user name"
                            disabled={loading}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: WB_UI.radius.card } }}
                        />

                        <TextField
                            fullWidth
                            label="Email *"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="subuser@example.com"
                            disabled={loading}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: WB_UI.radius.card } }}
                        />

                        <TextField
                            fullWidth
                            label="Password *"
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                            placeholder="Enter password (min 6 characters)"
                            disabled={loading}
                            helperText="At least 6 characters"
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: WB_UI.radius.card } }}
                        />

                        {needsBwc ? (
                            <TextField
                                fullWidth
                                label="WhatsApp credits (BWC)"
                                name="bwc"
                                type="number"
                                value={formData.bwc}
                                onChange={handleChange}
                                placeholder="0"
                                disabled={loading}
                                helperText="Optional for WhatsApp roles; 0 copies config without transferring credits."
                                inputProps={{ min: 0 }}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: WB_UI.radius.card } }}
                            />
                        ) : null}
                    </Box>
                </DialogContent>

                <Divider />
                <DialogActions sx={{ px: 2.5, py: 2, gap: 1 }}>
                    <Button onClick={handleClose} disabled={loading} sx={WB_SX.secondaryButton}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || roleChoices.length === 0 || formData.roles.length === 0}
                        sx={WB_SX.primaryButton}
                        startIcon={loading ? <CircularProgress size={18} sx={{ color: "inherit" }} /> : <Save sx={{ fontSize: 18 }} />}
                    >
                        {loading ? "Creating…" : "Create user"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default CreateSubUserModal;
